import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FolderPlus, Link2, Plus } from '@digital-twins/design-system/icons'
import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/routes/routes'
import { PageHeader } from '@/components/layout/PageHeader'
import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { KnowledgeLibraryHeaderProps as KnowledgeLibraryHeaderBaseProps } from '@/types/components/knowledge-library-header'

export interface KnowledgeLibraryHeaderProps extends KnowledgeLibraryHeaderBaseProps {
  /** Opens New collection on the Resources tab while the Library can load. */
  onNewCollection?: () => void
  /**
   * Resources-tab tools below the browse tabs (the mobile collections grid
   * and the Themes slot). Search lives on the filter row.
   */
  children?: ReactNode
}

export function KnowledgeLibraryHeader({
  socialEnabled = false,
  connectorsEnabled = false,
  view = 'docs',
  canLoadLibrary = false,
  canManageTrackers = false,
  onAddLinks,
  onAddTracker,
  onNewCollection,
  children,
}: KnowledgeLibraryHeaderProps) {
  const { t } = useTranslation('app')
  const tabClassName =
    'h-10 rounded-none border-b-2 border-transparent bg-transparent px-0 py-2 font-normal shadow-none hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
  const newCollectionButton =
    canLoadLibrary && onNewCollection ? (
      <Button
        variant="outline"
        shape="pill"
        className="h-9 px-4"
        onClick={onNewCollection}
        data-testid="library-new-collection"
      >
        <FolderPlus className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('library.collections.newCollection')}
      </Button>
    ) : null

  const pageAction = !canLoadLibrary || view === 'connectors' || view === 'brands' || view === 'boards' || view === 'creatives'
    ? null
    : view === 'audiences'
      ? (
          <Button
            asChild
            variant="cta"
            shape="pill"
            className="h-9 px-4"
            data-testid="audiences-create-button"
          >
            <Link href={ROUTES.audiencesNew}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('audiences.create')}
            </Link>
          </Button>
        )
      : view === 'social'
      ? canManageTrackers
        ? (
            <Button
              variant="cta"
              shape="pill"
              className="h-9 px-4"
              onClick={onAddTracker}
              data-testid="social-new-topic"
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('socialListening.topicTab.newTopic')}
            </Button>
          )
        : null
      : (
          <div className="flex flex-wrap items-center gap-2">
            {newCollectionButton}
            <Button
              variant="outline"
              shape="pill"
              className="h-9 px-4"
              onClick={onAddLinks}
              data-testid="knowledge-add-links"
            >
              <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('library.addLinks')}
            </Button>
            <Button
              asChild
              variant="cta"
              shape="pill"
              className="h-9 px-4"
              data-testid="knowledge-upload-cta"
            >
              <Link href={ROUTES.knowledgeUpload}>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('library.upload')}
              </Link>
            </Button>
          </div>
        )

  const tabs = canLoadLibrary ? (
    <div className="flex flex-col-reverse gap-x-6 gap-y-2 border-b border-border sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 max-w-full overflow-x-auto">
        <TabsList
          aria-label={t('library.title')}
          className="h-auto justify-start gap-6 rounded-none bg-transparent p-0"
          data-testid="knowledge-view-tabs"
        >
          <TabsTrigger
            value="docs"
            className={tabClassName}
            data-testid="knowledge-view-tab-docs"
          >
            {t('library.tabs.resources')}
          </TabsTrigger>
          {(['brands', 'creatives'] as const).map((creativeView) => (
            <TabsTrigger key={creativeView} value={creativeView} className={tabClassName} data-testid={`knowledge-view-tab-${creativeView}`}>
              {t(`creativeLibrary.${creativeView}`)}
            </TabsTrigger>
          ))}
          {socialEnabled ? (
            <TabsTrigger
              value="social"
              className={tabClassName}
              data-testid="knowledge-view-tab-social"
            >
              {t('library.tabs.social')}
            </TabsTrigger>
          ) : null}
          <TabsTrigger
            value="audiences"
            className={tabClassName}
            data-testid="knowledge-view-tab-audiences"
          >
            {t('library.tabs.audiences')}
          </TabsTrigger>
          {connectorsEnabled ? (
            <TabsTrigger
              value="connectors"
              className={tabClassName}
              data-testid="knowledge-view-tab-connectors"
            >
              {t('connectors.title')}
            </TabsTrigger>
          ) : null}
        </TabsList>
      </div>
    </div>
  ) : null

  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="knowledge-header">
      <PageHeader
        variant="lobby"
        title={t('library.title')}
        description={t(view === 'brands' || view === 'boards' || view === 'creatives' ? 'creativeLibrary.libraryHint' : 'library.subtitle')}
        action={pageAction ?? undefined}
      />
      {tabs}
      {children}
    </div>
  )
}
